import { Box, Button, CircularProgress } from '@mui/material'
import { ReactElement, Suspense, lazy, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import { ShowNotesIcon } from 'src/v3/core/icons'
import { usePageTour } from 'src/v3/tours/hooks/use-page-tour'
import {
  fetchShowNotesLibrary,
  fetchLinkedServices,
  selectShowNotesLibrary,
  selectShowNotesLibraryStatus,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectedLinkedServiceChanged,
} from '../create.slice'
import { deleteShowNotes, migrateShowNotes } from '../utils/api'
import { CREATE_SHOW_NOTES_URLS } from '../constants'
import LibraryRowBase from '../shared/components/library-row-base'
import { useLibraryFilters } from '../shared/hooks/use-library-filters'
import LibraryPageLayout from '../shared/components/library-page-layout'

let CreateShowNotesModal = lazy(
  () => import('./components/create-show-notes-modal'),
)

let ModalFallback = (
  <Box
    className="fixed inset-0 z-50 flex items-center justify-center"
    role="status"
    aria-label="Loading modal"
  >
    <CircularProgress aria-hidden="true" />
  </Box>
)

export default function ShowNotesLibraryPage(): ReactElement {
  useScrollToTop()
  usePageTour('show-notes-library')
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let showNotesList = useAppSelector(selectShowNotesLibrary)
  let showNotesLibraryStatus = useAppSelector(selectShowNotesLibraryStatus)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  useAutoFetch(showNotesLibraryStatus, fetchShowNotesLibrary)
  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  let {
    linkedServiceFilter,
    courseFilter,
    search,
    setLinkedServiceFilter,
    setCourseFilter,
    setSearch,
    resetFilters,
    isFiltered,
    filteredItems: filteredShowNotes,
    distinctCourses,
  } = useLibraryFilters(showNotesList)

  let [createShowNotesOpen, setCreateShowNotesOpen] = useState(false)
  let [deleteShowNotesID, setDeleteShowNotesID] = useState<string | null>(null)
  let [isDeleting, setIsDeleting] = useState(false)

  let { showSuccess, showError, NotificationComponent } = useNotifications()

  let handleEdit = (id: string) => {
    navigate(CREATE_SHOW_NOTES_URLS.EDIT(id), {
      state: {
        backLabel: 'Back to Show Notes Library',
        backPath: CREATE_SHOW_NOTES_URLS.LIBRARY,
      },
    })
  }

  let handleMigrate = (showNotesID: string) => {
    migrateShowNotes({ contentID: showNotesID })
      .then(() => {
        dispatch(fetchShowNotesLibrary())
        showSuccess('Show notes migrated successfully')
      })
      .catch(() => showError('Failed to migrate show notes'))
  }

  let handleDeleteConfirm = () => {
    if (!deleteShowNotesID) return
    setIsDeleting(true)
    deleteShowNotes(deleteShowNotesID)
      .then(() => {
        dispatch(fetchShowNotesLibrary())
        showSuccess('Show notes deleted successfully')
        setDeleteShowNotesID(null)
      })
      .catch(() => showError('Failed to delete show notes'))
      .finally(() => setIsDeleting(false))
  }

  return (
    <>
      <LibraryPageLayout
        pageTitle="Show Notes Library"
        createButton={
          <Button
            variant="contained"
            size="large"
            startIcon={<ShowNotesIcon />}
            onClick={() => setCreateShowNotesOpen(true)}
          >
            Create Show Notes
          </Button>
        }
        itemCountLabel={`${filteredShowNotes.length} show notes`}
        linkedServices={linkedServices}
        filters={{
          linkedServiceFilter,
          courseFilter,
          search,
          onLinkedServiceFilterChange: (v) => {
            setLinkedServiceFilter(v)
            setCourseFilter('all')
            dispatch(selectedLinkedServiceChanged(v))
          },
          onCourseFilterChange: setCourseFilter,
          onSearchChange: setSearch,
          onResetFilters: resetFilters,
          isFiltered,
          distinctCourses,
          filteredItemCount: filteredShowNotes.length,
          totalItemCount: showNotesList.length,
        }}
        isLoading={showNotesLibraryStatus === 'pending'}
        emptyMessage="No show notes found."
        noResultsMessage="No show notes match your filters."
        loadingAriaLabel="Loading show notes"
        itemsLayout="list"
        deleteDialog={{
          open: !!deleteShowNotesID,
          onClose: () => setDeleteShowNotesID(null),
          onConfirm: handleDeleteConfirm,
          title: 'Delete Show Notes',
          itemName: showNotesList.find((sn) => sn.id === deleteShowNotesID)
            ?.name,
          isDeleting,
        }}
      >
        {filteredShowNotes.map((sn) => (
          <LibraryRowBase
            key={sn.id}
            item={sn}
            contentType="showNotes"
            onEdit={() => handleEdit(sn.id)}
            onDelete={() => setDeleteShowNotesID(sn.id)}
            onMigrate={() => handleMigrate(sn.id)}
          />
        ))}
      </LibraryPageLayout>

      {createShowNotesOpen && (
        <Suspense fallback={ModalFallback}>
          <CreateShowNotesModal
            isOpen
            onClose={() => setCreateShowNotesOpen(false)}
            linkedServices={linkedServices}
            defaultLinkedServiceID={
              linkedServiceFilter !== 'all' ? linkedServiceFilter : ''
            }
            onSuccess={(id) => {
              dispatch(fetchShowNotesLibrary())
              setCreateShowNotesOpen(false)
              handleEdit(id)
            }}
            onFailure={() => showError('Failed to create show notes')}
          />
        </Suspense>
      )}

      {NotificationComponent}
    </>
  )
}
